import React, { useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { Link } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import get from "lodash.get";
import { Helmet } from "react-helmet-async";
import Texture from "@mui/icons-material/Texture";

import { useUser } from "../../../contexts/UserContext";
import SubInternalLayout from "../../layout/SubInternalLayout";
import { useFormData } from "../../../hooks/useFormData";
import { useApiPut } from "../../../hooks/useApi";
import SpinIcon from "../../common/SpinIcon";

const TaxInfo = () => {
  const { user, updateUser } = useUser();
  const { formData, hasChanged, onChange } = useFormData({
    taxId: get(user, "helper.taxId") || "",
  });

  const save = useApiPut("/users/profile", (res) => {
    toast.success("Your changes have been successfully saved.");
    updateUser(res);
  });
  const [downloading, setDownloading] = useState(false);

  return (
    <SubInternalLayout title={`Tax Info`}>
      <Helmet>
        <title>Tax Info | Account | Manana</title>
        <meta name="description" content="Manana Help" />
      </Helmet>
      <div className="field-list-item">
        <Form.Group>
          <Form.Control
            type="text"
            name="taxId"
            placeholder="Tax ID or Social Security Number"
            onChange={onChange}
            value={formData.taxId}
          />
        </Form.Group>

        <Link
          to=""
          disabled
          style={{
            display: "block",
            margin: "1.5rem 0",
            textDecoration: "none",
            fontWeight: 600,
            fontSize: "1.15rem",
            color: 'grey',
            cursor: 'unset'
          }}
        >
          <Texture /> DOWNLOAD MY 1099
        </Link>
        <Link
          to=""
          onClick={async (e) => {
            e.preventDefault();
            if (downloading) return;
            try {
              setDownloading(true);
              // Fetch payouts JSON
              const url = `${process.env.REACT_APP_API_URL}/users/stripe-payouts`;
              const { data } = await axios.post(url, {});
              if (!data || data.ok === false) {
                throw new Error(data?.msg || "Unable to fetch payouts.");
              }

              const period = data.period || {};
              const userInfo = data.user || {};
              const payouts = data.payouts || [];

              // Build PDF
              const doc = new jsPDF({ unit: "pt", format: "letter" });
              const pageWidth = doc.internal.pageSize.getWidth();
              const pageHeight = doc.internal.pageSize.getHeight();
              const margin = 40;
              let y = margin;

              const line = (text, opts = {}) => {
                const { size = 11, bold = false } = opts;
                if (bold) doc.setFont(undefined, "bold"); else doc.setFont(undefined, "normal");
                doc.setFontSize(size);
                doc.text(String(text), margin, y);
                y += size + 6;
              };
              const writeParagraph = (text, opts = {}) => {
                const { size = 11 } = opts;
                doc.setFontSize(size);
                const lines = doc.splitTextToSize(String(text), pageWidth - margin * 2);
                lines.forEach((ln) => {
                  doc.text(ln, margin, y);
                  y += size + 5;
                });
              };

              // Attempt to load logo from public folder
              let logoDataUrl = null;
              try {
                const res = await fetch('/manana-logo.png', { cache: 'no-store' });
                if (res.ok) {
                  const blob = await res.blob();
                  const toDataURL = (b) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(b);
                  });
                  logoDataUrl = await toDataURL(blob);
                }
              } catch (_) {
                // ignore, will fall back to text title
              }

              // Header
              doc.setFontSize(18);
              doc.setFont(undefined, "bold");
              doc.text("Proof of Income", margin, y);
              y += 36; // extra spacing after title
              doc.setFont(undefined, "normal");

              const fullName = userInfo?.name || (user?.name ?? "");
              // Pretty date formatting (e.g., Feb 20th 2025)
              const toOrdinal = (n) => {
                const v = n % 100;
                if (v >= 11 && v <= 13) return `${n}th`;
                switch (n % 10) {
                  case 1: return `${n}st`;
                  case 2: return `${n}nd`;
                  case 3: return `${n}rd`;
                  default: return `${n}th`;
                }
              };
              const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const fmtPretty = (d) => {
                if (!d) return "";
                const dt = d instanceof Date ? d : new Date(`${d}T00:00:00`);
                return `${monthShort[dt.getMonth()]} ${toOrdinal(dt.getDate())} ${dt.getFullYear()}`;
              };
              const toKey = (d) => {
                const dt = d instanceof Date ? d : new Date(`${d}T00:00:00`);
                const mm = String(dt.getMonth() + 1).padStart(2, "0");
                const dd = String(dt.getDate()).padStart(2, "0");
                return `${dt.getFullYear()}${mm}${dd}`;
              };
              // Place Manana logo (from public) below the title
              if (logoDataUrl) {
                try {
                  // Determine intrinsic image size to avoid stretching
                  const img = new Image();
                  img.src = logoDataUrl;
                  // Prefer decode() when available for a clean await
                  if (img.decode) {
                    await img.decode();
                  } else {
                    await new Promise((resolve, reject) => {
                      img.onload = resolve;
                      img.onerror = reject;
                    });
                  }

                  const PX_TO_PT = 0.75; // 96 CSS px per inch vs 72 pt per inch
                  const imgWpt = (img.naturalWidth || 0) * PX_TO_PT;
                  const imgHpt = (img.naturalHeight || 0) * PX_TO_PT;
                  const capWidth = 120; // reduce logo size further
                  const maxLogoWidth = Math.min(pageWidth - margin * 2, capWidth);
                  const drawW = Math.min(imgWpt || maxLogoWidth, maxLogoWidth);
                  const scale = imgWpt ? (drawW / imgWpt) : 1;
                  const drawH = (imgHpt || 0) * scale;

                  doc.addImage(logoDataUrl, 'PNG', margin, y - 4, drawW, drawH || 0);
                  y += (drawH || 0) + 24; // spacing after logo
                } catch (_) {
                  // Fallback to text if addImage fails
                  doc.setFont(undefined, 'bold');
                  doc.setFontSize(12);
                  doc.text("Manana", margin, y);
                  y += 28;
                }
              } else {
                // Fallback to text if image unavailable
                doc.setFont(undefined, 'bold');
                doc.setFontSize(12);
                doc.text("Manana", margin, y);
                y += 28;
              }
              // Date and Subject lines with extra spacing between them
              line(`Date: ${fmtPretty(new Date())}`);
              y += 8;
              line(`Subject: Proof of Income for ${fullName}`);
              // No explicit period line per request
              y += 24;

              // Intro paragraph above Income (no specific start date)
              writeParagraph(
                `This letter is to confirm that ${fullName} works as an independent contractor with Manana.`
              );
              y += 6;
              writeParagraph(
                `Below is a summary of their earnings for the past 365 days`
              );
              y += 18;

              doc.setFont(undefined, "bold");
              doc.setFontSize(10);
              const colX = [margin, margin + 180, margin + 320, margin + 420];
              const writeRow = (vals, bold = false) => {
                if (y > pageHeight - margin) {
                  doc.addPage();
                  y = margin;
                }
                doc.setFont(undefined, bold ? "bold" : "normal");
                doc.setFontSize(10);
                vals.forEach((v, i) => {
                  doc.text(String(v), colX[i] || colX[colX.length - 1], y);
                });
                y += 16;
              };
              writeRow(["Date", "Amount", "Currency", "Status"], true);
              doc.setFont(undefined, "normal");

              if (!payouts.length) {
                writeRow(["No payouts in the selected period.", "", "", ""], false);
              } else {
                payouts.forEach((p) => {
                  const date = p.arrival_date
                    ? new Date(p.arrival_date * 1000)
                    : new Date(p.created * 1000);
                  const dateStr = fmtPretty(date);
                  const amount = (p.amount || 0) / 100;
                  const amountStr = amount.toLocaleString(undefined, { style: "currency", currency: (p.currency || "usd").toUpperCase() });
                  writeRow([
                    dateStr,
                    amountStr,
                    (p.currency || "").toUpperCase(),
                    p.status || "",
                  ]);
                });
              }

              const defaultStart = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
              const defaultEnd = new Date();
              const startKey = toKey(period.start_date || defaultStart);
              const endKey = toKey(period.end_date || defaultEnd);
              const fileName = `proof-of-income_${startKey}_to_${endKey}.pdf`;
              doc.save(fileName);
              toast.success("Your proof of income is downloading.");
            } catch (err) {
              const msg = err?.response?.data?.msg || err?.message || "Unable to generate PDF.";
              toast.error(msg);
            } finally {
              setDownloading(false);
            }
          }}
          style={{
            display: "block",
            margin: "1.5rem 0",
            textDecoration: "none",
            fontWeight: 600,
            fontSize: "1.15rem",
            opacity: downloading ? 0.6 : 1,
            pointerEvents: downloading ? "none" : "auto",
          }}
        >
          {downloading ? (
            <>
              <SpinIcon /> Generating proof of income...
            </>
          ) : (
            <>
              <Texture /> DOWNLOAD MY PROOF OF INCOME
            </>
          )}
        </Link>
      </div>

      <div className="text-end mt-3">
        <Button
          type="button"
          onClick={() =>
            save.mutate({
              "helper.taxId": formData.taxId,
            })
          }
          disabled={!hasChanged || save.isLoading}
        >
          {save.isLoading ? <SpinIcon /> : "Save Changes"}
        </Button>
      </div>

      <br />
      <br />
    </SubInternalLayout>
  );
};

export default TaxInfo;
